import { OidcSecurityService, UserDataResult } from 'angular-auth-oidc-client';
import { Inject, Injectable, DOCUMENT } from '@angular/core';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, map, take, tap } from 'rxjs/operators';
import { ElectronService } from '@core/services/electron.service';
import { UserService } from '@core/services/user.service';
import { U2bUserApiService } from '@core/services/api/u2b-user.api-service';
import { Router } from '@angular/router';

import { WelcomeSplashService } from '@core/components/welcome-splash/welcome-splash.service';

let windowLoginPopup: Window;

@Injectable({providedIn: 'root'})
export class AuthService {

    private isLoggingIn = false;

    constructor(@Inject(DOCUMENT) private document: Document,
                private oidcSecurityService: OidcSecurityService,
                private electronService: ElectronService,
                private userApiService: U2bUserApiService,
                private userService: UserService,
                private router: Router,
                private welcomeSplashService: WelcomeSplashService) {
    }

    get isLoggedIn$(): Observable<boolean> {
        return this.oidcSecurityService
            .isAuthenticated$
            .pipe(map(result => result.isAuthenticated));
    }

    get token(): Observable<string> {
        return this.oidcSecurityService.getAccessToken();
    }

    get userDataResult$(): Observable<UserDataResult> {
        return this.oidcSecurityService.userData$;
    }

    get userData$(): Observable<any> {
        return this.userDataResult$.pipe(map(result => result.userData));
    }

    checkAuth(url?: string): Observable<boolean> {
        this.isLoggingIn = false;

        if (this.electronService.isElectronApp()) {
            this._closeLoginPopup();
        }

        if (url) {
            this.oidcSecurityService.checkAuth(url).subscribe();
            return EMPTY;
        }

        return this.oidcSecurityService
            .checkAuth()
            .pipe(
                take(1),
                map(({isAuthenticated}) => {
                    if (isAuthenticated) {
                        this.loadCurrentUser();
                    } else {
                        this.doLogin();
                    }
                    return isAuthenticated;
                }),
                catchError((error) => {
                    console.error(error);
                    return of(false);
                })
            );
    }

    doLogin(): void {
        if (this.isLoggingIn) {
            return;
        }

        this.isLoggingIn = true;

        if (this.electronService.isElectronApp()) {
            const urlHandler = (authUrl: string) => {
                windowLoginPopup = this.document.defaultView.open(authUrl, '_blank', 'nodeIntegration=no,popup=true,prompt=login');
            };
            this.oidcSecurityService.authorize(undefined, {urlHandler});
        } else {
            this.oidcSecurityService.authorize(undefined, {customParams: {prompt: 'login'}});
        }
    }

    signOut(): void {
        // id_token_hint weglassen – Keycloak wirft bei bereits revoziertem oder
        // abgelaufenem Token einen Fehler ("Es ist ein Fehler aufgetreten").
        // Ohne customParams leitet Keycloak sauber auf postLogoutRedirectUri weiter.
        this.welcomeSplashService.resetSessionFlag();
        this.oidcSecurityService.logoffAndRevokeTokens().subscribe();
    }

    loadCurrentUser(): void {
        this.userApiService
            .getCurrent()
            .pipe(
                tap(appUser => {
                    if (!appUser) {
                        this.signOut();
                    }
                }),
                catchError(error => {
                    console.error('[AuthService] loadCurrentUser fehlgeschlagen:', error);
                    // 401 = Token ungültig oder Backend kann Keycloak nicht erreichen → Session leeren,
                    // nicht auf /errors/500 (sonst Deadlock: eingeloggt, API scheitert, 500, erneut Login).
                    if (error?.status === 401) {
                        this.signOut();
                    } else {
                        this.router.navigate(['errors', '500']);
                    }
                    return of(null);
                }),
            )
            .subscribe(appUser => {
                if (appUser) {
                    this.userService.user = appUser;
                    this.welcomeSplashService.showOncePerSession(appUser.firstName);
                }
            });
    }

    private _closeLoginPopup(): void {
        if (windowLoginPopup) {
            (windowLoginPopup.window || windowLoginPopup).close();
        }
    }
}
